import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { BASE_URL } from '../components/constants';
import { authHeaders } from '../utils/authHeaders';

// Async thunk for fetching shortcuts by task ID
export const fetchShortcutsByTask = createAsyncThunk(
  'shortcuts/fetchShortcutsByTask',
  async ({ taskId, includeInactive = false }, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/shortcuts/task/${taskId}`, {
        params: { include_inactive: includeInactive },
        ...authHeaders(),
      });
      return response.data.shortcuts;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch shortcuts');
    }
  }
);

// Async thunk for fetching active shortcuts by task (for regular users in analyze view)
export const fetchActiveShortcutsByTask = createAsyncThunk(
  'shortcuts/fetchActiveShortcutsByTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/shortcuts/task/${taskId}`, {
        params: { include_inactive: false },
        ...authHeaders(),
      });
      return response.data.shortcuts;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch shortcuts');
    }
  }
);

// Async thunk for creating a new shortcut
export const createShortcut = createAsyncThunk(
  'shortcuts/createShortcut',
  async (shortcutData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/shortcuts`, shortcutData, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create shortcut');
    }
  }
);

// Async thunk for updating a shortcut
export const updateShortcut = createAsyncThunk(
  'shortcuts/updateShortcut',
  async (shortcutData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/shortcuts`, shortcutData, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update shortcut');
    }
  }
);

// Async thunk for deleting a shortcut
export const deleteShortcut = createAsyncThunk(
  'shortcuts/deleteShortcut',
  async (shortcutId, { rejectWithValue }) => {
    try {
      const response = await axios.delete(`${BASE_URL}/shortcuts/${shortcutId}`, authHeaders());
      return { shortcutId, ...response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete shortcut');
    }
  }
);

// Async thunk for updating shortcuts order
export const updateShortcutsOrder = createAsyncThunk(
  'shortcuts/updateShortcutsOrder',
  async (orderData, { rejectWithValue }) => {
    try {
      const response = await axios.put(`${BASE_URL}/shortcuts/order`, orderData, authHeaders());
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update shortcuts order');
    }
  }
);

// Async thunk for executing a shortcut
export const executeShortcut = createAsyncThunk(
  'shortcuts/executeShortcut',
  async ({ shortcutId, content, historyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${BASE_URL}/shortcuts/execute`, {
        shortcut_id: shortcutId,
        content: content,
        history_id: historyId
      }, authHeaders());
      // Include historyId in the result so CommentSection can identify which message this belongs to
      return { ...response.data, historyId };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to execute shortcut');
    }
  }
);

const shortcutsSlice = createSlice({
  name: 'shortcuts',
  initialState: {
    shortcuts: [],
    activeShortcuts: [],
    currentTaskId: null,
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    loading: false,
    executing: false,
    executionResult: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearStatus: (state) => {
      state.status = 'idle';
    },
    clearExecutionResult: (state) => {
      state.executionResult = null;
    },
    clearShortcuts: (state) => {
      state.shortcuts = [];
      state.activeShortcuts = [];
      state.currentTaskId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch shortcuts by task
      .addCase(fetchShortcutsByTask.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(fetchShortcutsByTask.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.shortcuts = action.payload;
        state.loading = false;
      })
      .addCase(fetchShortcutsByTask.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.loading = false;
      })
      // Fetch active shortcuts by task
      .addCase(fetchActiveShortcutsByTask.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActiveShortcutsByTask.fulfilled, (state, action) => {
        state.activeShortcuts = action.payload;
        state.loading = false;
      })
      .addCase(fetchActiveShortcutsByTask.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Create shortcut
      .addCase(createShortcut.pending, (state) => {
        state.loading = true;
      })
      .addCase(createShortcut.fulfilled, (state) => {
        state.loading = false;
        state.status = 'idle';
      })
      .addCase(createShortcut.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Update shortcut
      .addCase(updateShortcut.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShortcut.fulfilled, (state) => {
        state.loading = false;
        state.status = 'idle';
      })
      .addCase(updateShortcut.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Delete shortcut
      .addCase(deleteShortcut.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteShortcut.fulfilled, (state, action) => {
        state.loading = false;
        state.shortcuts = state.shortcuts.filter(
          shortcut => shortcut.shortcut_id !== action.payload.shortcutId
        );
      })
      .addCase(deleteShortcut.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Update shortcuts order
      .addCase(updateShortcutsOrder.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateShortcutsOrder.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateShortcutsOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Execute shortcut
      .addCase(executeShortcut.pending, (state) => {
        state.executing = true;
        state.executionResult = null;
      })
      .addCase(executeShortcut.fulfilled, (state, action) => {
        state.executing = false;
        state.executionResult = action.payload;
      })
      .addCase(executeShortcut.rejected, (state, action) => {
        state.executing = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearStatus, clearExecutionResult, clearShortcuts } = shortcutsSlice.actions;
export default shortcutsSlice.reducer;
